import dataStorageManager, { BucketDefaultIDs, DMS_Operation } from '../../managers/data/DataStorageManager'
import dataQueryManager from '../../managers/network/DataQueryManager'
import { IRequest } from './RequestsServices'
import { IUser } from './UsersServices'

export enum EstadosCredencial {
  INHABILITADA = 0, // No permite el acceso
  SOLO_USUARIO = 1, // Permite acceso con usos limitados
  PASAJEROS = 2, // Permite acceso libre
}

// La logica credencial describe como se comporta la credencial respecto al estado del horario
// Debe poder manejar 2 eventos: Dentro del horario y Fuera del horario
export enum LogicaCredencial {
  SIEMPRE_INACTIVA = 0x0000,
  SIMPLE = 0x0001, // Activa manual dentro del horario, inactiva fuera del horario
  AUTO_SIMPLE = 0x0011, // Activa automaticamente dentro del horario, inactiva fuera del horario
  EMERGENCIA = 0x1100, // Inactiva dentro del horario, activa automaticamente fuera del horario
  OPERACIONES = 0x0100, // Inactiva dentro del horario, activa manual fuera del horario
  SIMPLE_COMPLETO = 0x0101, // Activa dentro y fuera del horario
  AUTO_SIMPLE_COMPLETO = 0x1111, // Activa automaticamente dentro y fuera del horario
  PATRON_EMERGENCIA = 0x1102, // Activa fuera del horario, activa sin limites dentro del horario
  PATRON = 0x0002, // Activa sin limites dentro del horario, inactiva fuera del horario
  PATRON_AUTO = 0x0012, // Activa sin limites dentro del horario, activa automaticamente fuera del horario
}

export enum EstadoRolAlternativo {
  INACTIVO = 0,
  ACTIVO = 1,
}

/**
 * Define quién tiene el permiso de abrir la puerta de sillas de ruedas (Puerta B)
 * desde el lado muelle. Solo puede asignarse a quien esté en EstadosCredencial.PASAJEROS (2).
 * Son mutuamente excluyentes: Armador XOR Patrón, nunca Tripulante.
 */
export enum PuertaB {
  NINGUNO = 'ninguno',
  ARMADOR = 'armador',
  PATRON = 'patron',
}

export interface IInterino {
  id_usuario: number
  id_rol_alternativo: number
  id_solicitud: number
}

export interface OperatedInterino extends IInterino {
  usuarioData: IUser
  rolAlternativoData: IRolAlternativo
  solicitudData: IRequest | undefined
}

export interface IRolAlternativo {
  id: number
  nombre: string
  descripcion: string
  logica_credencial: LogicaCredencial
  estado: EstadoRolAlternativo
  horario: number // FK a IHorario.id (0 = sin horario)
  nivel_credencial: 0 | 1 | 2 // nivel de acceso: 0=sin acceso, 1=limitado, 2=libre
  puerta_b: 0 | 1 // 0=sin permiso puerta B, 1=puede abrir puerta B
}

export interface OperatedRolAlternativo extends IRolAlternativo {
  interinos: IInterino[]
}

/**
 * Configuración de estados de credencial aplicada globalmente al momento de iniciar
 * la autorización de zarpe. Define qué estado (1 o 2) recibe cada perfil y quién
 * tiene el permiso de puerta B (silla de ruedas).
 *
 * Reglas de negocio:
 * - estado_armador siempre >= 1 (armador/representante nunca se inhabilita)
 * - puerta_b solo puede asignarse al perfil que tenga estado = 2
 * - puerta_b es XOR: Armador o Patrón, nunca ambos, nunca Tripulante
 * - Si un usuario aparece en múltiples perfiles, gana el mayor estado (regla MAX)
 */
export interface IConfiguracionCredencial {
  id: number
  nombre: string
  descripcion: string
  estado_armador: 1 | 2 // estado de credencial para armador y su representante (nunca 0)
  estado_patron: 0 | 1 | 2 // estado de credencial para el patrón (0 = inhabilitado)
  estado_tripulante: 0 | 1 | 2 // estado de credencial para los tripulantes (0 = inhabilitado)
  puerta_b: PuertaB // quién puede abrir la puerta de sillas de ruedas desde el lado muelle
  uso_limite: number // máximo de usos en estado 1 por ciclo (0 = sin límite)
  tiempo_reinicio: number // minutos para resetear el contador de usos en estado 1
  activo: boolean
  // El acceso de los esporádicos se rige por su rol alternativo, no por este tipo,
  // así que el campo se retiró del formulario (RN37/RN49) y nunca se persistió en
  // backend. Se mantiene opcional para no romper consumidores; sin uso en UI.
  tipo_credencial?: 'normal' | 'temporal'
}

/**
 * Registro singleton que indica cuál IConfiguracionCredencial está activa globalmente.
 * Se actualiza por el ADMIN cuando cambia la configuración en vigor del sistema.
 */
export interface IConfigGlobalCredencial {
  configuracion_credencial_id: number
  actualizado_por: number
}

export default class InterinosServices {
  private bucketID: string | null = null

  constructor() {
    this.bucketID = dataStorageManager.registerBucket<IRolAlternativo>(this.getAllRolesAlternativos.bind(this), {
      refreshRatio: 5 * 60 * 1000,
      bucketID: BucketDefaultIDs.ROLES_ALTERNATIVOS,
      operations: new Map<string, DMS_Operation>([
        [
          'insertRolAlternativo',
          {
            operation: this.insertRolAlternativo.bind(this),
            reloadAfter: true,
          },
        ],
        [
          'updateRolAlternativo',
          {
            operation: this.updateRolAlternativo.bind(this),
            reloadAfter: true,
          },
        ],
      ]),
    })

    dataStorageManager.registerBucket<IConfiguracionCredencial>(this.getAllConfiguracionesCredencial.bind(this), {
      refreshRatio: 5 * 60 * 1000,
      bucketID: BucketDefaultIDs.CONFIGURACIONES_CREDENCIAL,
      operations: new Map<string, DMS_Operation>([
        [
          'insertConfiguracionCredencial',
          {
            operation: this.insertConfiguracionCredencial.bind(this),
            reloadAfter: true,
          },
        ],
        [
          'updateConfiguracionCredencial',
          {
            operation: this.updateConfiguracionCredencial.bind(this),
            reloadAfter: true,
          },
        ],
      ]),
    })

    dataStorageManager.registerBucket<IConfigGlobalCredencial>(this.getConfigGlobalCredencial.bind(this), {
      refreshRatio: 5 * 60 * 1000,
      bucketID: BucketDefaultIDs.CONFIG_GLOBAL_CREDENCIAL,
      operations: new Map<string, DMS_Operation>([
        [
          'updateConfigGlobalCredencial',
          {
            operation: this.updateConfigGlobalCredencial.bind(this),
            reloadAfter: true,
          },
        ],
      ]),
    })
  }

  startup() {}

  // ─── Roles Alternativos ──────────────────────────────────────────────────

  async getAllRolesAlternativos(): Promise<IRolAlternativo[]> {
    try {
      const response = await dataQueryManager.addQuery<IRolAlternativo | IRolAlternativo[]>({
        query: `${import.meta.env.VITE_BASE_URL}/representante_interino/1/op_select_rol_alternativo`,
        operation: 'POST',
        headers: { 'Content-Type': 'application/json' },
        params: [],
      })
      if (!response.data) return []
      const list = Array.isArray(response.data) ? response.data : [response.data]
      return list.map((r) => ({
        ...r,
        // descripcion es nullable en BD: el backend devuelve null. Normalizamos a ''
        // para que la búsqueda/orden (que llaman .normalize()) no reviente la vista.
        descripcion: r.descripcion ?? '',
        // horario es nullable (NULL = sin horario = 24/7). El front usa 0 como "Sin horario";
        // convertimos NULL→0 para que horarioNombre/el selector lo muestren correctamente.
        horario: (r.horario ?? 0) as number,
        nivel_credencial: (r.nivel_credencial ?? 0) as 0 | 1 | 2,
        puerta_b: ((r.puerta_b ?? 0) > 0 ? 1 : 0) as 0 | 1,
      }))
    } catch (error) {
      console.error('Error fetching roles alternativos:', error)
    }
    return []
  }

  async insertRolAlternativo(rolAlternativoData: IRolAlternativo): Promise<boolean> {
    try {
      const body = {
        op_insert_rol_alternativo: {
          nombre: rolAlternativoData.nombre,
          descripcion: rolAlternativoData.descripcion,
          logica_credencial: rolAlternativoData.logica_credencial ?? 0,
          estado: rolAlternativoData.estado ?? 0,
          horario: rolAlternativoData.horario ?? 0,
          nivel_credencial: rolAlternativoData.nivel_credencial ?? 0,
          puerta_b: rolAlternativoData.puerta_b ?? 0,
        },
      }
      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/representante_interino/1/op_insert_rol_alternativo`,
        operation: 'POST',
        headers: { 'Content-Type': 'application/json' },
        params: [],
        body,
      })
      if (response.data && typeof response.data === 'number') return true
    } catch (error) {
      console.error('Error inserting rol alternativo:', error)
    }
    return false
  }

  async updateRolAlternativo(rolAlternativoData: IRolAlternativo): Promise<boolean> {
    try {
      const body = {
        op_update_rol_alternativo: {
          nombre: rolAlternativoData.nombre,
          descripcion: rolAlternativoData.descripcion,
          logica_credencial: rolAlternativoData.logica_credencial ?? 0,
          estado: rolAlternativoData.estado ?? 0,
          horario: rolAlternativoData.horario ?? 0,
          nivel_credencial: rolAlternativoData.nivel_credencial ?? 0,
          puerta_b: rolAlternativoData.puerta_b ?? 0,
          id: rolAlternativoData.id,
        },
      }
      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/representante_interino/1/op_update_rol_alternativo`,
        operation: 'POST',
        headers: { 'Content-Type': 'application/json' },
        params: [],
        body,
      })
      if (response.data && typeof response.data === 'number') return true
    } catch (error) {
      console.error('Error updating rol alternativo:', error)
    }
    return false
  }

  // ─── Configuraciones de Credencial ───────────────────────────────────────

  async getAllConfiguracionesCredencial(): Promise<IConfiguracionCredencial[]> {
    try {
      const response = await dataQueryManager.addQuery<IConfiguracionCredencial | IConfiguracionCredencial[]>({
        query: `${import.meta.env.VITE_BASE_URL}/representante_interino/1/op_select_config_credencial`,
        operation: 'POST',
        headers: { 'Content-Type': 'application/json' },
        params: [],
      })
      if (!response.data) return []
      const list = Array.isArray(response.data) ? response.data : [response.data]
      // Normalizar activo: el backend puede devolver 0/1 en lugar de boolean
      return list.map((c) => ({ ...c, activo: Boolean(c.activo) }))
    } catch (error) {
      console.error('Error fetching configuraciones credencial:', error)
    }
    return []
  }

  async insertConfiguracionCredencial(data: IConfiguracionCredencial): Promise<boolean> {
    try {
      const body = {
        op_insert_config_credencial: {
          nombre: data.nombre,
          descripcion: data.descripcion,
          estado_armador: data.estado_armador,
          estado_patron: data.estado_patron,
          estado_tripulante: data.estado_tripulante,
          puerta_b: data.puerta_b,
          uso_limite: data.uso_limite,
          tiempo_reinicio: data.tiempo_reinicio,
          activo: data.activo,
        },
      }
      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/representante_interino/1/op_insert_config_credencial`,
        operation: 'POST',
        headers: { 'Content-Type': 'application/json' },
        params: [],
        body,
      })
      if (response.data && typeof response.data === 'number') return true
    } catch (error) {
      console.error('Error inserting configuracion credencial:', error)
    }
    return false
  }

  async updateConfiguracionCredencial(data: IConfiguracionCredencial): Promise<boolean> {
    try {
      const body = {
        op_update_config_credencial: {
          nombre: data.nombre,
          descripcion: data.descripcion,
          estado_armador: data.estado_armador,
          estado_patron: data.estado_patron,
          estado_tripulante: data.estado_tripulante,
          puerta_b: data.puerta_b,
          uso_limite: data.uso_limite,
          tiempo_reinicio: data.tiempo_reinicio,
          activo: !!data.activo,
          id: data.id,
        },
      }
      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/representante_interino/1/op_update_config_credencial`,
        operation: 'POST',
        headers: { 'Content-Type': 'application/json' },
        params: [],
        body,
      })
      if (response.data && typeof response.data === 'number') return true
    } catch (error) {
      console.error('Error updating configuracion credencial:', error)
    }
    return false
  }

  // ─── Configuración Global de Credencial ──────────────────────────────────

  async getConfigGlobalCredencial(): Promise<IConfigGlobalCredencial[]> {
    try {
      const response = await dataQueryManager.addQuery<IConfigGlobalCredencial | IConfigGlobalCredencial[]>({
        query: `${import.meta.env.VITE_BASE_URL}/representante_interino/1/op_select_config_global_credencial`,
        operation: 'POST',
        headers: { 'Content-Type': 'application/json' },
        params: [],
      })
      if (!response.data) return []
      return Array.isArray(response.data) ? response.data : [response.data]
    } catch (error) {
      console.error('Error fetching config global credencial:', error)
    }
    return []
  }

  async updateConfigGlobalCredencial(data: IConfigGlobalCredencial): Promise<boolean> {
    try {
      const body = {
        op_update_config_global_credencial: {
          actualizado_por: data.actualizado_por,
          configuracion_credencial_id: data.configuracion_credencial_id,
        },
      }
      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/representante_interino/1/op_update_config_global_credencial`,
        operation: 'POST',
        headers: { 'Content-Type': 'application/json' },
        params: [],
        body,
      })
      if (response.data && typeof response.data === 'number') return true
    } catch (error) {
      console.error('Error updating config global credencial:', error)
    }
    return false
  }
}
